import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import "@/styles/landing.css";

/* ============================================================
   Inline icon set (kept as React components for portability).
============================================================ */
const I = {
  Shield: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>),
  Zap: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>),
  Lock: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>),
  Activity: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>),
  Server: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/><path d="M6 6h.01M6 18h.01"/></svg>),
  FileText: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>),
  Users: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>),
  Eye: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>),
  Clock: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>),
  Terminal: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>),
  Settings: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9c.36.17.69.41 1 .72l.08.06A2 2 0 0 1 21 12z"/></svg>),
  Globe: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>),
  Cpu: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><rect x="4" y="4" width="16" height="16" rx="2"/><rect x="9" y="9" width="6" height="6"/><line x1="9" y1="1" x2="9" y2="4"/><line x1="15" y1="1" x2="15" y2="4"/><line x1="9" y1="20" x2="9" y2="23"/><line x1="15" y1="20" x2="15" y2="23"/><line x1="20" y1="9" x2="23" y2="9"/><line x1="20" y1="14" x2="23" y2="14"/><line x1="1" y1="9" x2="4" y2="9"/><line x1="1" y1="14" x2="4" y2="14"/></svg>),
  HardDrive: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><line x1="22" y1="12" x2="2" y2="12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/><line x1="6" y1="16" x2="6.01" y2="16"/><line x1="10" y1="16" x2="10.01" y2="16"/></svg>),
  TrendingUp: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>),
  BarChart: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>),
  AlertTriangle: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>),
  Check: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" {...p}><polyline points="20 6 9 17 4 12"/></svg>),
  Bell: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>),
  ArrowRight: (p: any) => (<svg width="14" height="14" viewBox="0 0 14 14" fill="none" {...p}><path d="M3 7h8m0 0L7 3m4 4l-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>),
  Play: (p: any) => (<svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" {...p}><polygon points="5 3 19 12 5 21 5 3"/></svg>),
  ChevronDown: (p: any) => (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...p}><polyline points="6 9 12 15 18 9"/></svg>),
  X: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>),
  Github: (p: any) => (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12 0C5.37 0 0 5.37 0 12c0 5.3 3.44 9.8 8.21 11.39.6.11.82-.26.82-.58v-2.17c-3.34.72-4.04-1.41-4.04-1.41-.55-1.39-1.34-1.76-1.34-1.76-1.09-.75.08-.73.08-.73 1.21.08 1.84 1.24 1.84 1.24 1.07 1.83 2.81 1.3 3.5 1 .1-.78.42-1.3.76-1.6-2.66-.3-5.47-1.33-5.47-5.93 0-1.31.47-2.38 1.24-3.22-.12-.3-.54-1.52.12-3.17 0 0 1-.32 3.3 1.23.96-.27 1.98-.4 3-.4s2.04.13 3 .4c2.3-1.55 3.3-1.23 3.3-1.23.66 1.65.24 2.87.12 3.17.77.84 1.24 1.91 1.24 3.22 0 4.61-2.81 5.62-5.48 5.92.43.37.81 1.1.81 2.22v3.29c0 .32.22.7.82.58C20.56 21.8 24 17.3 24 12c0-6.63-5.37-12-12-12z"/></svg>),
  Windows: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M0 3.5L9.75 2.25v9.5H0zM10.75 2.1L24 0v11.5H10.75zM0 12.75h9.75v9.5L0 20.5zM10.75 12.75H24V24l-13.25-2.1z"/></svg>),
  Linux: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M12.5 0a3.16 3.16 0 0 0-3.06 3.36c0 .59.13 1.16.34 1.56-.36.15-.7.36-.97.66-.5.55-.78 1.3-.95 2.07-.34 1.55-.36 3.34-1.05 4.4-.79 1.21-1.86 2.38-2.5 3.66-.31.62-.5 1.27-.46 1.95-.21.21-.38.46-.55.66-.16.18-.4.32-.72.45-.64.28-.52.9-.3 1.25.21.36.57.6 1.07.56.34-.02.7-.13 1.04-.3.16-.08.36-.1.56-.14.21-.05.46-.12.67-.3.1-.08.17-.18.27-.26.16-.11.3-.24.38-.34.05-.06.06-.14.09-.2.07-.03.18-.05.25-.1.13-.1.17-.22.21-.37l.08-.27c.02-.09-.03-.21.02-.36.05-.15.19-.3.4-.47.14-.12.24-.27.33-.43.08-.16.12-.28.17-.4.1-.23.12-.42.06-.6-.14-.4-.57-.7-1.02-.7-.13 0-.26.02-.37.07-.15.07-.26.18-.34.33-.11.18-.28.32-.39.5-.08.13-.18.22-.28.31-.15.15-.25.26-.34.34l.0-.02c0-.02 0-.04.01-.05.02-.07.02-.16.02-.2 0-.04 0-.18 0-.23 0-1.13.07-2.3.33-3.2.12-.4.3-.79.53-1.13.22-.33.46-.62.71-.84.5-.44.75-.77.9-1.04.09-.17.13-.3.16-.42.06-.32.02-.59-.1-.83-.12-.23-.3-.42-.56-.52.07-.1.18-.23.28-.37.21-.3.36-.65.36-1.09a1.21 1.21 0 0 0-1.05-1.21z"/></svg>),
  Slack: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zM18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zM15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"/></svg>),
  Teams: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M20.625 8.127h-5.5a1.5 1.5 0 0 0-1.5 1.5v7.2h2.5v4.7l5-4.7h1a1.5 1.5 0 0 0 1.5-1.5V10.627a2.5 2.5 0 0 0-3-2.5zM8.125 6.627a3 3 0 1 0-3-3 3 3 0 0 0 3 3zm2 2.5h-4a3 3 0 0 0-3 3v5.5h10v-5.5a3 3 0 0 0-3-3zm8-2.5a2.5 2.5 0 1 0-2.5-2.5 2.5 2.5 0 0 0 2.5 2.5z"/></svg>),
  Mail: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>),
  AD: (p: any) => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...p}><circle cx="12" cy="8" r="4"/><path d="M4 21v-2a4 4 0 0 1 4-4h8a4 4 0 0 1 4 4v2"/><circle cx="18" cy="5" r="2"/></svg>),
};

/* ============================================================
   Animation tick hook (drives all animated visuals).
============================================================ */
function useTick() {
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const loop = (now: number) => {
      setT((now - start) / 1000);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);
  return t;
}

/* ============================================================
   Hero 3D scene
============================================================ */
function HeroScene() {
  const ref = useRef<HTMLDivElement>(null);
  const t = useTick();
  const [mouse, setMouse] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const r = ref.current?.getBoundingClientRect();
      if (!r) return;
      const x = (e.clientX - r.left - r.width / 2) / r.width;
      const y = (e.clientY - r.top - r.height / 2) / r.height;
      setMouse({ x: Math.max(-0.5, Math.min(0.5, x)), y: Math.max(-0.5, Math.min(0.5, y)) });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  const rotY = Math.sin(t * 0.25) * 8 + mouse.x * 20;
  const rotX = -20 + mouse.y * -10;

  return (
    <div className="scene" ref={ref}>
      <WireGlobe t={t} />
      <ParticleField t={t} />

      <div style={{
        position: "absolute", inset: 0,
        transformStyle: "preserve-3d",
        transform: `rotateX(${rotX}deg) rotateY(${rotY}deg)`,
        transition: "transform .3s cubic-bezier(.2,.7,.2,1)",
      }}>
        <ServerTower t={t} />
      </div>

      <FloatingCards t={t} mouse={mouse} />
    </div>
  );
}

function ServerTower({ t }: { t: number }) {
  const units = [
    { label: "db-prod-01", status: "ok", cpu: 34 },
    { label: "api-eu-02", status: "ok", cpu: 61 },
    { label: "cache-03", status: "warn", cpu: 89 },
    { label: "worker-04", status: "ok", cpu: 22 },
    { label: "gateway-05", status: "ok", cpu: 51 },
  ];
  const unitH = 52;
  const totalH = unitH * units.length;
  const baseY = totalH / 2;
  const float = Math.sin(t * 0.8) * 6;

  return (
    <div style={{
      position: "absolute",
      left: "50%", top: "50%",
      transformStyle: "preserve-3d",
      transform: `translate(-50%, -50%) translateY(${float}px)`,
    }}>
      {units.map((u, i) => {
        const y = i * unitH - baseY + unitH / 2;
        const w = 240, h = 44, d = 140;
        const scanX = ((t * 60 + i * 30) % (w + 40)) - 20;
        const statusColor = u.status === "ok" ? "var(--good)" : "var(--warn)";
        const face: any = {
          position: "absolute",
          left: -w / 2, top: -h / 2,
          width: w, height: h,
          borderRadius: 6,
          border: "1px solid var(--line-2)",
          background: "linear-gradient(180deg, oklch(0.28 0.05 var(--hue)), oklch(0.2 0.04 var(--hue)))",
          boxShadow: "inset 0 1px 0 oklch(1 0 0 / 0.08), 0 8px 30px -10px oklch(0 0 0 / 0.6)",
        };
        return (
          <div key={i} style={{ position: "absolute", transformStyle: "preserve-3d", transform: `translateY(${y}px)` }}>
            {/* back */}
            <div style={{ ...face, transform: `translateZ(${-d / 2}px) rotateY(180deg)`, background: "linear-gradient(180deg, oklch(0.18 0.03 var(--hue)), oklch(0.14 0.03 var(--hue)))" }} />
            {/* front */}
            <div style={{ ...face, transform: `translateZ(${d / 2}px)`, display: "flex", alignItems: "center", padding: "0 14px", overflow: "hidden", gap: 10 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
              <div style={{ fontFamily: "Geist Mono, monospace", fontSize: 11, color: "var(--fg)", letterSpacing: "0.04em" }}>{u.label}</div>
              <div style={{ flex: 1, height: 4, background: "oklch(0.14 0.03 var(--hue))", borderRadius: 2, overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${u.cpu}%`, background: "linear-gradient(90deg, var(--accent), var(--accent-2))", boxShadow: "0 0 8px var(--accent)" }} />
              </div>
              <div style={{ fontFamily: "Geist Mono, monospace", fontSize: 10, color: "var(--fg-3)" }}>{u.cpu}%</div>
              <div style={{ position: "absolute", left: scanX, top: 0, width: 30, height: "100%", background: "linear-gradient(90deg, transparent, var(--accent) 50%, transparent)", opacity: 0.5, filter: "blur(4px)", pointerEvents: "none" }} />
            </div>
            {/* top */}
            <div style={{ position: "absolute", left: -w / 2, top: -h / 2, width: w, height: d, background: "linear-gradient(180deg, oklch(0.34 0.05 var(--hue)), oklch(0.24 0.04 var(--hue)))", border: "1px solid var(--line-2)", borderRadius: 4, transform: `rotateX(90deg) translateZ(${h / 2}px)` }}>
              <div style={{ position: "absolute", inset: "8px 14px", background: "repeating-linear-gradient(90deg, oklch(0.4 0.05 var(--hue) / 0.4) 0 2px, transparent 2px 8px)" }} />
            </div>
            {/* sides */}
            <div style={{ position: "absolute", top: -h / 2, left: -d / 2, width: d, height: h, transform: `rotateY(-90deg) translateZ(${w / 2}px)`, background: "linear-gradient(90deg, oklch(0.16 0.03 var(--hue)), oklch(0.22 0.04 var(--hue)))", border: "1px solid var(--line-2)", borderRadius: 4 }} />
            <div style={{ position: "absolute", top: -h / 2, left: -d / 2, width: d, height: h, transform: `rotateY(90deg) translateZ(${w / 2}px)`, background: "linear-gradient(90deg, oklch(0.22 0.04 var(--hue)), oklch(0.16 0.03 var(--hue)))", border: "1px solid var(--line-2)", borderRadius: 4 }} />
          </div>
        );
      })}
    </div>
  );
}

function WireGlobe({ t }: { t: number }) {
  const rot = t * 10;
  const size = 520;
  return (
    <div style={{
      position: "absolute", left: "50%", top: "50%",
      width: size, height: size,
      transform: `translate(-50%, -50%) rotateX(65deg) rotateZ(${rot}deg)`,
      transformStyle: "preserve-3d",
      opacity: 0.5,
      pointerEvents: "none",
    }}>
      <svg viewBox={`-${size / 2} -${size / 2} ${size} ${size}`} width={size} height={size} style={{ overflow: "visible" }}>
        {[1, 0.86, 0.7, 0.52, 0.3].map((s, i) => (
          <circle key={i} r={(size / 2) * s} fill="none" stroke="oklch(0.7 0.08 var(--hue))" strokeOpacity={0.3 - i * 0.04} strokeWidth="1" />
        ))}
        {Array.from({ length: 12 }).map((_, i) => {
          const a = (i / 12) * Math.PI * 2;
          return <line key={i} x1={0} y1={0} x2={Math.cos(a) * size / 2} y2={Math.sin(a) * size / 2} stroke="oklch(0.7 0.08 var(--hue))" strokeOpacity={0.15} strokeWidth="1" />;
        })}
        {Array.from({ length: 18 }).map((_, i) => {
          const a = (i / 18) * Math.PI * 2 + t * 0.2;
          const r = (size / 2) * (0.3 + (i % 4) * 0.17);
          const pulse = (Math.sin(t * 2 + i) + 1) / 2;
          return (
            <g key={i}>
              <circle cx={Math.cos(a) * r} cy={Math.sin(a) * r} r={3 + pulse * 2} fill="var(--accent)" opacity={0.6 + pulse * 0.4} />
              <circle cx={Math.cos(a) * r} cy={Math.sin(a) * r} r={8 + pulse * 6} fill="var(--accent)" opacity={0.1} />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function ParticleField({ t }: { t: number }) {
  const parts = useMemo(() => Array.from({ length: 40 }).map(() => ({
    x: Math.random(), y: Math.random(), r: Math.random() * 2 + 0.5, phase: Math.random() * 6,
  })), []);
  return (
    <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} preserveAspectRatio="none" viewBox="0 0 100 100">
      {parts.map((p, i) => {
        const tw = (Math.sin(t * 2 + p.phase) + 1) / 2;
        return <circle key={i} cx={p.x * 100} cy={p.y * 100} r={p.r * 0.2} fill="var(--accent)" opacity={0.2 + tw * 0.6} />;
      })}
    </svg>
  );
}

const floatCardStyle: any = {
  background: "linear-gradient(180deg, oklch(0.24 0.04 var(--hue) / 0.9), oklch(0.17 0.03 var(--hue) / 0.95))",
  border: "1px solid var(--line-2)",
  borderRadius: 14,
  padding: 14,
  backdropFilter: "blur(16px) saturate(140%)",
  WebkitBackdropFilter: "blur(16px) saturate(140%)",
  boxShadow: "0 20px 60px -20px oklch(0 0 0 / 0.6), inset 0 1px 0 oklch(1 0 0 / 0.08)",
};

function FloatingCards({ t, mouse }: { t: number; mouse: { x: number; y: number } }) {
  const uptime = 99.992 + Math.sin(t * 0.3) * 0.003;
  const latency = Math.round(42 + Math.sin(t * 0.8) * 6);
  const reqs = Math.round(12400 + Math.sin(t * 0.5) * 800);

  return (
    <>
      <div style={{ position: "absolute", left: "-4%", top: "14%", transform: `translate(${mouse.x * -16}px, ${mouse.y * -8}px)`, transition: "transform .4s cubic-bezier(.2,.7,.2,1)" }}>
        <FloatGraph t={t} />
      </div>
      <div style={{ position: "absolute", right: "-6%", top: "22%", transform: `translate(${mouse.x * 20}px, ${mouse.y * 10}px)`, transition: "transform .4s cubic-bezier(.2,.7,.2,1)" }}>
        <FloatStat label="UPTIME" value={uptime.toFixed(3) + "%"} sub="30-day avg" positive />
      </div>
      <div style={{ position: "absolute", right: "2%", bottom: "10%", transform: `translate(${mouse.x * 14}px, ${mouse.y * -6}px)`, transition: "transform .4s cubic-bezier(.2,.7,.2,1)" }}>
        <FloatAlert />
      </div>
      <div style={{ position: "absolute", left: "2%", bottom: "4%", transform: `translate(${mouse.x * -12}px, ${mouse.y * 6}px)`, transition: "transform .4s cubic-bezier(.2,.7,.2,1)" }}>
        <FloatStat label="P50 LATENCY" value={latency + "ms"} sub={reqs.toLocaleString() + " req/min"} />
      </div>
    </>
  );
}

function FloatStat({ label, value, sub, positive }: { label: string; value: string; sub: string; positive?: boolean }) {
  return (
    <div style={{ ...floatCardStyle, minWidth: 170 }}>
      <div style={{ fontFamily: "Geist Mono, monospace", fontSize: 10, color: "var(--fg-3)", letterSpacing: "0.14em" }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 11, color: positive ? "var(--good)" : "var(--fg-3)", marginTop: 4, fontFamily: "Geist Mono, monospace" }}>
        {positive && "▲ "}{sub}
      </div>
    </div>
  );
}

function FloatGraph({ t }: { t: number }) {
  const pts = Array.from({ length: 30 }).map((_, i) => {
    const x = (i / 29) * 160;
    const y = 30 + Math.sin(i * 0.5 + t * 0.8) * 10 + Math.sin(i * 0.3 + t * 0.3) * 6;
    return [x, y] as const;
  });
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(" ");
  const area = `${path} L160,60 L0,60 Z`;
  return (
    <div style={{ ...floatCardStyle, width: 200 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontFamily: "Geist Mono, monospace", fontSize: 10, color: "var(--fg-3)", letterSpacing: "0.14em" }}>TRAFFIC</div>
        <div className="status-pill"><span className="status-pill__dot" />LIVE</div>
      </div>
      <svg viewBox="0 0 160 60" width="100%" height="60">
        <defs>
          <linearGradient id="ga" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#ga)" />
        <path d={path} stroke="var(--accent)" strokeWidth="1.5" fill="none" />
      </svg>
    </div>
  );
}

function FloatAlert() {
  return (
    <div style={{ ...floatCardStyle, width: 220 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--warn)", boxShadow: "0 0 8px var(--warn)" }} />
        <div style={{ fontFamily: "Geist Mono, monospace", fontSize: 10, color: "var(--fg-3)", letterSpacing: "0.14em" }}>ALERT · 12s AGO</div>
      </div>
      <div style={{ fontSize: 13, color: "var(--fg)", lineHeight: 1.4 }}>SSL cert <b style={{ color: "var(--warn)" }}>expires in 14 days</b> on api.servspect.com</div>
      <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
        <div style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "var(--bg-3)", border: "1px solid var(--line)", color: "var(--fg-2)" }}>Snooze</div>
        <div style={{ fontSize: 11, padding: "3px 8px", borderRadius: 6, background: "var(--accent)", color: "oklch(0.1 0.05 var(--hue))", fontWeight: 500 }}>Renew</div>
      </div>
    </div>
  );
}

/* ============================================================
   Feature visualisations
============================================================ */
function SecureViz() {
  const t = useTick();
  const rot = t * 15;
  return (
    <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", perspective: 1200 }}>
      <div style={{ transformStyle: "preserve-3d", transform: `rotateY(${rot}deg) rotateX(20deg)` }}>
        <div style={{
          width: 180, height: 210,
          background: "conic-gradient(from 0deg, oklch(0.4 0.15 var(--hue) / 0.6), oklch(0.6 0.2 var(--hue) / 0.9), oklch(0.4 0.15 var(--hue) / 0.6))",
          clipPath: "polygon(50% 0%, 100% 20%, 100% 70%, 50% 100%, 0 70%, 0 20%)",
          boxShadow: "0 0 80px var(--accent)", position: "relative",
        }}>
          <div style={{
            position: "absolute", inset: 6, background: "oklch(0.16 0.03 var(--hue))",
            clipPath: "polygon(50% 0%, 100% 20%, 100% 70%, 50% 100%, 0 70%, 0 20%)",
            display: "grid", placeItems: "center", color: "var(--accent)",
          }}>
            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4"/><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </div>
        </div>
      </div>
      {[0, 1, 2, 3].map((i) => {
        const a = t * 0.5 + (i * Math.PI) / 2;
        const r = 160;
        return (
          <div key={i} style={{
            position: "absolute",
            left: `calc(50% + ${Math.cos(a) * r}px)`,
            top: `calc(50% + ${Math.sin(a) * r * 0.4}px)`,
            transform: "translate(-50%,-50%)",
            padding: "4px 8px", borderRadius: 6,
            background: "oklch(0.22 0.04 var(--hue) / 0.9)",
            border: "1px solid var(--line-2)",
            fontFamily: "Geist Mono, monospace", fontSize: 9, color: "var(--fg-2)", letterSpacing: "0.1em",
          }}>
            {["HMAC", "AES", "TOKEN", "HTTPS"][i]}
          </div>
        );
      })}
      <div style={{
        position: "absolute", bottom: 20, left: 20, right: 20,
        padding: "10px 12px", borderRadius: 8,
        background: "oklch(0.12 0.03 var(--hue))",
        border: "1px solid var(--line)",
        fontFamily: "Geist Mono, monospace", fontSize: 11, color: "var(--good)",
      }}>
        ✓ Payload verified · SHA256 · nonce-protected
      </div>
    </div>
  );
}

function DeployViz() {
  const t = useTick();
  const lines = [
    { c: "var(--fg-3)", text: "# Windows · PowerShell" },
    { c: "var(--accent)", text: "PS> iwr -UseB ss.io/agent | iex" },
    { c: "var(--fg-3)", text: "[*] Downloading agent v4.8.0" },
    { c: "var(--good)", text: "[✓] HMAC keypair generated" },
    { c: "var(--good)", text: "[✓] Service registered: ServSpectAgent" },
    { c: "var(--fg-3)", text: "# Linux · bash" },
    { c: "var(--accent)", text: "$ curl -sL ss.io/a | sudo bash" },
    { c: "var(--good)", text: "[✓] Enrolled as srv-web-prod-02" },
  ];
  const visible = Math.min(lines.length, Math.floor(t * 1.5) % (lines.length + 4));
  return (
    <div style={{ position: "absolute", inset: 20, borderRadius: 12, background: "oklch(0.1 0.02 var(--hue))", border: "1px solid var(--line)", overflow: "hidden", boxShadow: "inset 0 1px 0 oklch(1 0 0 / 0.05)" }}>
      <div style={{ padding: "8px 12px", borderBottom: "1px solid var(--line)", display: "flex", gap: 6, alignItems: "center" }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--bad)" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--warn)" }} />
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--good)" }} />
        <div style={{ flex: 1, textAlign: "center", fontFamily: "Geist Mono, monospace", fontSize: 11, color: "var(--fg-3)" }}>terminal — agent install</div>
      </div>
      <div style={{ padding: 14, fontFamily: "Geist Mono, monospace", fontSize: 12, lineHeight: 1.9 }}>
        {lines.slice(0, visible).map((l, i) => (<div key={i} style={{ color: l.c }}>{l.text}</div>))}
        {visible < lines.length && <div style={{ color: "var(--accent)" }}>▌</div>}
      </div>
    </div>
  );
}

function PerfViz() {
  const t = useTick();
  const gauges = [
    { label: "CPU", v: 0.34 + Math.sin(t * 0.8) * 0.1, color: "var(--accent)" },
    { label: "MEM", v: 0.62 + Math.sin(t * 0.5 + 1) * 0.05, color: "var(--accent-2)" },
    { label: "DSK", v: 0.22 + Math.sin(t * 0.7 + 2) * 0.08, color: "var(--accent-3)" },
    { label: "NET", v: 0.48 + Math.sin(t * 1.1 + 3) * 0.15, color: "var(--good)" },
  ];
  return (
    <div style={{ position: "absolute", inset: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      {gauges.map((g, i) => (
        <div key={i} style={{ background: "oklch(0.14 0.03 var(--hue))", border: "1px solid var(--line)", borderRadius: 12, padding: 14, position: "relative", overflow: "hidden" }}>
          <div style={{ fontFamily: "Geist Mono, monospace", fontSize: 10, color: "var(--fg-3)", letterSpacing: "0.14em" }}>{g.label}</div>
          <div style={{ fontSize: 28, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 4, color: g.color }}>
            {Math.round(g.v * 100)}<span style={{ fontSize: 14, color: "var(--fg-3)", fontWeight: 400 }}>%</span>
          </div>
          <svg viewBox="0 0 140 40" width="100%" height="40" style={{ marginTop: 6 }}>
            <path d={Array.from({ length: 40 }).map((_, j) => {
              const x = (j / 39) * 140;
              const y = 30 - (Math.sin(j * 0.4 + t * 0.6 + i) * 8 + g.v * 15);
              return (j === 0 ? "M" : "L") + x + "," + y;
            }).join(" ")} stroke={g.color} strokeWidth="1.5" fill="none" />
          </svg>
          <div style={{ position: "absolute", right: 10, top: 10, width: 6, height: 6, borderRadius: "50%", background: g.color, boxShadow: `0 0 8px ${g.color}` }} />
        </div>
      ))}
    </div>
  );
}

function LogsViz() {
  const t = useTick();
  const entries = [
    { sev: "ERR", c: "var(--bad)", src: "win/sec", msg: "Failed login · admin@dc-01 (4625)" },
    { sev: "WRN", c: "var(--warn)", src: "linux/auth", msg: "sshd: 3 failed attempts from 203.0.113.8" },
    { sev: "INF", c: "var(--accent)", src: "win/sys", msg: "Service ServSpectAgent started" },
    { sev: "INF", c: "var(--accent)", src: "journald", msg: "systemd: nginx.service · active" },
    { sev: "ERR", c: "var(--bad)", src: "win/ps", msg: "Encoded command blocked by policy" },
    { sev: "INF", c: "var(--good)", src: "win/sec", msg: "4624 · Successful logon · Mila.K" },
  ];
  const shift = ((Math.floor(t * 0.8) % entries.length) + entries.length) % entries.length;
  return (
    <div style={{ position: "absolute", inset: 20, borderRadius: 12, background: "oklch(0.1 0.02 var(--hue))", border: "1px solid var(--line)", overflow: "hidden" }}>
      <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)", display: "flex", gap: 8, fontSize: 11, fontFamily: "Geist Mono, monospace" }}>
        <span style={{ color: "var(--fg-3)", letterSpacing: "0.1em" }}>UNIFIED LOG STREAM · LIVE</span>
        <span style={{ marginLeft: "auto", color: "var(--good)" }}>● {Math.round(2400 + t * 10).toLocaleString()} events/min</span>
      </div>
      <div style={{ fontFamily: "Geist Mono, monospace", fontSize: 11 }}>
        {entries.map((_, i) => {
          const idx = (i + shift) % entries.length;
          const e = entries[idx];
          return (
            <div key={i} style={{ display: "grid", gridTemplateColumns: "40px 70px 1fr", gap: 10, padding: "9px 14px", borderBottom: "1px solid var(--line)", alignItems: "center" }}>
              <span style={{ color: e.c, fontWeight: 600 }}>{e.sev}</span>
              <span style={{ color: "var(--fg-3)" }}>{e.src}</span>
              <span style={{ color: "var(--fg-2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.msg}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ComplianceViz() {
  const t = useTick();
  const frameworks = [
    { n: "ISO 27001", p: 0.92 },
    { n: "SOC 2", p: 0.78 },
    { n: "GDPR", p: 0.88 },
    { n: "NIS2", p: 0.64 },
    { n: "CIS v8", p: 0.71 },
  ];
  return (
    <div style={{ position: "absolute", inset: 20, display: "grid", gridTemplateRows: "auto 1fr", gap: 14 }}>
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1, padding: 12, borderRadius: 10, background: "oklch(0.14 0.03 var(--hue))", border: "1px solid var(--line)" }}>
          <div style={{ fontSize: 10, color: "var(--fg-3)", fontFamily: "Geist Mono, monospace", letterSpacing: "0.12em" }}>CONTROLS PASSED</div>
          <div style={{ fontSize: 22, fontWeight: 600, color: "var(--good)", marginTop: 2 }}>284 / 312</div>
        </div>
        <div style={{ flex: 1, padding: 12, borderRadius: 10, background: "oklch(0.14 0.03 var(--hue))", border: "1px solid var(--line)" }}>
          <div style={{ fontSize: 10, color: "var(--fg-3)", fontFamily: "Geist Mono, monospace", letterSpacing: "0.12em" }}>EVIDENCE FILES</div>
          <div style={{ fontSize: 22, fontWeight: 600, marginTop: 2 }}>1,428</div>
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "oklch(0.14 0.03 var(--hue))", borderRadius: 10, border: "1px solid var(--line)" }}>
        {frameworks.map((f, i) => {
          const p = Math.min(1, f.p + Math.sin(t * 0.3 + i) * 0.02);
          return (
            <div key={f.n}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4, fontFamily: "Geist Mono, monospace" }}>
                <span style={{ color: "var(--fg)" }}>{f.n}</span>
                <span style={{ color: "var(--fg-3)" }}>{Math.round(p * 100)}%</span>
              </div>
              <div style={{ height: 5, background: "oklch(0.2 0.03 var(--hue))", borderRadius: 3, overflow: "hidden" }}>
                <div style={{ width: `${p * 100}%`, height: "100%", background: "linear-gradient(90deg, var(--accent), var(--accent-3))", boxShadow: "0 0 6px var(--accent)" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ManagementViz() {
  const t = useTick();
  return (
    <div style={{ position: "absolute", inset: 20, perspective: 1000, display: "grid", placeItems: "center" }}>
      <div style={{ transformStyle: "preserve-3d", transform: `rotateX(50deg) rotateZ(${t * 8}deg)` }}>
        <div style={{
          width: 70, height: 70, borderRadius: "50%",
          background: "linear-gradient(135deg, var(--accent), var(--accent-2))",
          boxShadow: "0 0 40px var(--accent)",
          display: "grid", placeItems: "center",
          color: "oklch(0.12 0.05 var(--hue))",
          transform: "translate3d(-50%, -50%, 20px)",
          position: "absolute", left: "50%", top: "50%",
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/></svg>
        </div>
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i / 8) * Math.PI * 2;
          const r = 120;
          return (
            <div key={i} style={{
              position: "absolute", left: "50%", top: "50%",
              transform: `translate3d(calc(-50% + ${Math.cos(a) * r}px), calc(-50% + ${Math.sin(a) * r}px), 0)`,
              width: 36, height: 36, borderRadius: 8,
              background: "oklch(0.22 0.04 var(--hue) / 0.9)",
              border: "1px solid var(--line-2)",
              display: "grid", placeItems: "center",
              color: "var(--accent)",
            }}>
              {i % 2 === 0 ? <I.Windows /> : <I.Linux />}
            </div>
          );
        })}
        <svg style={{ position: "absolute", left: "50%", top: "50%", transform: "translate(-50%, -50%)", overflow: "visible", pointerEvents: "none" }} width="300" height="300" viewBox="-150 -150 300 300">
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <line key={i} x1={0} y1={0} x2={Math.cos(a) * 120} y2={Math.sin(a) * 120} stroke="var(--accent)" strokeOpacity="0.3" strokeDasharray="2 3" />;
          })}
        </svg>
      </div>
      <div style={{ position: "absolute", bottom: 14, left: 14, right: 14, padding: "8px 12px", borderRadius: 8, background: "oklch(0.12 0.03 var(--hue))", border: "1px solid var(--line)", display: "flex", justifyContent: "space-between", fontFamily: "Geist Mono, monospace", fontSize: 11 }}>
        <span style={{ color: "var(--fg-3)" }}>TENANTS · 14</span>
        <span style={{ color: "var(--fg)" }}>SERVERS · 2,431</span>
        <span style={{ color: "var(--good)" }}>HEALTHY · 99.2%</span>
      </div>
    </div>
  );
}

function AlertsViz() {
  const t = useTick();
  const alerts = [
    { ch: "Slack", c: "var(--bad)", title: "High CPU · srv-api-03", sub: "#ops-critical · 2m ago" },
    { ch: "Teams", c: "var(--warn)", title: "Disk 88% · db-prod-01", sub: "IT Operations · 4m ago" },
    { ch: "Email", c: "var(--good)", title: "Resolved: auth.app.io", sub: "on-call@ · 6m ago" },
  ];
  return (
    <div style={{ position: "absolute", inset: 20, display: "flex", flexDirection: "column", gap: 10, justifyContent: "center" }}>
      {alerts.map((a, i) => {
        const float = Math.sin(t * 0.6 + i * 0.2) * 4;
        return (
          <div key={i} style={{
            padding: "14px 16px", borderRadius: 12,
            background: "linear-gradient(180deg, oklch(0.24 0.04 var(--hue) / 0.9), oklch(0.17 0.03 var(--hue) / 0.95))",
            border: "1px solid var(--line-2)",
            boxShadow: "0 20px 40px -20px oklch(0 0 0 / 0.6)",
            transform: `translateY(${float}px)`,
            display: "flex", gap: 12, alignItems: "center",
          }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: "oklch(0.3 0.08 var(--hue) / 0.5)", display: "grid", placeItems: "center", color: a.c }}>
              {a.ch === "Slack" ? <I.Slack /> : a.ch === "Teams" ? <I.Teams /> : <I.Mail />}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: "var(--fg)", fontWeight: 500 }}>{a.title}</div>
              <div style={{ fontSize: 11, color: "var(--fg-3)", fontFamily: "Geist Mono, monospace" }}>{a.sub}</div>
            </div>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: a.c, boxShadow: `0 0 8px ${a.c}` }} />
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   Top-level chrome
============================================================ */
function Banner() {
  return (
    <div className="banner">
      <span className="banner__badge">NEW</span>
      <span>ServSpect Agent v4.8.0 Released — Token-free Authentication</span>
      <Link to="/knowledge-hub">Read Release Article <I.ArrowRight /></Link>
    </div>
  );
}

function Nav({ onSignIn, onSignUp }: { onSignIn: () => void; onSignUp: () => void }) {
  const scrollTo = (id: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <div className="nav">
      <div className="nav__brand">
        <div className="nav__logo" />
        <span>ServSpect</span>
        <span className="pill-os"><I.Github /> OPEN SOURCE</span>
      </div>
      <nav className="nav__links">
        <div className="nav__dropdown">
          <a href="#features">Features <I.ChevronDown /></a>
          <div className="nav__dropdown-menu">
            <a className="nav__dropdown-item" href="#feat-perf" onClick={scrollTo("feat-perf")}><b>Performance Monitoring</b><span>Real-time CPU, memory, disk, and network metrics</span></a>
            <a className="nav__dropdown-item" href="#feat-logs" onClick={scrollTo("feat-logs")}><b>Unified Log Management</b><span>Windows & Linux log collection and analysis</span></a>
            <a className="nav__dropdown-item" href="#feat-compliance" onClick={scrollTo("feat-compliance")}><b>Compliance & Frameworks</b><span>Compliance framework tracking and assessments</span></a>
            <a className="nav__dropdown-item" href="#feat-alerts" onClick={scrollTo("feat-alerts")}><b>Smart Alerting</b><span>Intelligent notifications via email and Slack</span></a>
          </div>
        </div>
        <div className="nav__dropdown">
          <a href="#resources">Resources <I.ChevronDown /></a>
          <div className="nav__dropdown-menu">
            <Link className="nav__dropdown-item" to="/install"><b>Installation Guide</b><span>Step-by-step agent deployment instructions</span></Link>
            <Link className="nav__dropdown-item" to="/privacy"><b>Privacy Policy</b><span>How we protect your data and privacy</span></Link>
            <Link className="nav__dropdown-item" to="/api-docs"><b>API Documentation</b><span>REST endpoints and integration reference</span></Link>
            <Link className="nav__dropdown-item" to="/knowledge-hub"><b>Knowledge Hub</b><span>Release notes, articles, and tutorials</span></Link>
          </div>
        </div>
        <Link to="/install">Install</Link>
      </nav>
      <div className="nav__actions">
        <button className="btn btn--ghost" onClick={onSignIn}>Sign In</button>
        <button className="btn btn--primary" onClick={onSignUp}>Get Started</button>
      </div>
    </div>
  );
}

function Hero({ onSignUp }: { onSignUp: () => void }) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const pills = [
    { icon: <I.Shield />, t: "Secure", s: "Server Monitoring" },
    { icon: <I.Zap />, t: "Zero-Config", s: "Agent Deployment" },
    { icon: <I.Lock />, t: "Device Token", s: "Authentication" },
    { icon: <I.Activity />, t: "Real-time", s: "Performance Metrics" },
    { icon: <I.Server />, t: "Centralized", s: "Server Management" },
    { icon: <I.FileText />, t: "Unified", s: "Windows & Linux Logs" },
  ];
  return (
    <section className="hero">
      <div className="container">
        <div className="hero__grid">
          <div>
            <Link to="/knowledge-hub" className="new-pill" style={{ textDecoration: "none" }}>
              <span className="new-pill__badge">NEW</span>
              Token-free HMAC Authentication
              <I.ArrowRight />
            </Link>
            <h1 className="display">Simple and Secure<br/><em>Server Monitoring.</em></h1>
            <p className="lede">ServSpect combines enterprise‑grade monitoring with Zero Trust security, providing a unified platform for Windows and Linux server performance metrics, unified log management, compliance tracking, and intelligent alerting.</p>
            <div className="hero__cta">
              <button className="btn btn--primary" onClick={onSignUp}>Get started — free <I.ArrowRight /></button>
              <button className="btn" onClick={() => navigate("/demo")}><I.Play /> View Demo</button>
            </div>
            <div className="hero-pills">
              {pills.map((p, i) => (
                <div key={i} className={"hero-pill" + (i === active ? " active" : "")} onClick={() => setActive(i)}>
                  <div className="hero-pill__icon">{p.icon}</div>
                  <div><b>{p.t}</b><span>{p.s}</span></div>
                </div>
              ))}
            </div>
          </div>
          <HeroScene />
        </div>
      </div>
    </section>
  );
}

function Integrations() {
  const items = [
    { i: <I.Windows />, l: "Windows Server" },
    { i: <I.Linux />, l: "Linux" },
    { i: <I.AD />, l: "Active Directory" },
    { i: <I.Slack />, l: "Slack" },
    { i: <I.Teams />, l: "Microsoft Teams" },
    { i: <I.Mail />, l: "Email SMTP" },
  ];
  return (
    <section style={{ padding: "20px 0 60px" }}>
      <div className="container">
        <div className="integrations">
          <div className="integrations__label">Integrates with</div>
          <div className="integrations__list">
            {items.map((it, i) => (
              <div className="integration" key={i}>{it.i} {it.l}</div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

type Bullet = { icon: ReactNode; text: string };
type FeatureRowDef = {
  id: string;
  reverse?: boolean;
  alt?: boolean;
  title: string;
  sub: string;
  bullets: Bullet[];
  visual: ReactNode;
};

function FeatureRow({ row, onExplore }: { row: FeatureRowDef; onExplore: () => void }) {
  return (
    <section id={row.id} className={"feature-row-section " + (row.alt ? "feature-row--alt" : "")}>
      <div className="container">
        <div className={"feature-row" + (row.reverse ? " reverse" : "")}>
          <div>
            <span className="eyebrow">Feature</span>
            <h2 className="feature-row__title">{row.title}</h2>
            <p className="feature-row__sub">{row.sub}</p>
            <ul>
              {row.bullets.map((b, i) => (
                <li key={i}><span className="icon">{b.icon}</span>{b.text}</li>
              ))}
            </ul>
            <button className="btn" onClick={onExplore}>Explore <I.ArrowRight /></button>
          </div>
          <div className="feature-row__visual">{row.visual}</div>
        </div>
      </div>
    </section>
  );
}

function FeatureSections({ onSignUp }: { onSignUp: () => void }) {
  const rows: FeatureRowDef[] = [
    {
      id: "feat-secure",
      title: "Secure Server Monitoring",
      sub: "Enterprise-grade security with zero-trust architecture for Windows and Linux",
      bullets: [
        { icon: <I.Users />, text: "Monitor unlimited Windows & Linux servers with multi-tenant isolation" },
        { icon: <I.Shield />, text: "HMAC-SHA256 signed payloads with replay protection" },
        { icon: <I.Lock />, text: "Device token authentication — no API keys in agents" },
        { icon: <I.Eye />, text: "Privacy mode for GDPR-compliant data collection" },
      ],
      visual: <SecureViz />,
    },
    {
      id: "feat-deploy", reverse: true, alt: true,
      title: "Cross-Platform Agent Deployment",
      sub: "Deploy monitoring agents in minutes on Windows and Linux servers",
      bullets: [
        { icon: <I.Clock />, text: "Install and configure agents in under 5 minutes" },
        { icon: <I.Terminal />, text: "PowerShell agent for Windows, Python agent for Linux" },
        { icon: <I.Settings />, text: "Secure config with NTFS ACL and file permission protection" },
        { icon: <I.Globe />, text: "Automatic server registration with enrollment tokens" },
      ],
      visual: <DeployViz />,
    },
    {
      id: "feat-perf",
      title: "Real-time Performance Metrics",
      sub: "Monitor CPU, memory, disk, and network with per-second granularity",
      bullets: [
        { icon: <I.Cpu />, text: "CPU usage, queue length, and per-core metrics" },
        { icon: <I.HardDrive />, text: "Disk I/O, SMART health, and volume monitoring" },
        { icon: <I.TrendingUp />, text: "Memory utilization and page file statistics" },
        { icon: <I.BarChart />, text: "Historical trends with customizable time ranges" },
      ],
      visual: <PerfViz />,
    },
    {
      id: "feat-logs", reverse: true, alt: true,
      title: "Unified Log Management",
      sub: "Centralized collection and analysis of Windows and Linux logs in one view",
      bullets: [
        { icon: <I.FileText />, text: "Windows Security, System, Application, and PowerShell logs" },
        { icon: <I.Terminal />, text: "Linux syslog, journald, auth, and kernel logs" },
        { icon: <I.AlertTriangle />, text: "Failed login detection and account lockout alerts" },
        { icon: <I.Eye />, text: "Filter by server, OS, severity, and search across all logs" },
      ],
      visual: <LogsViz />,
    },
    {
      id: "feat-compliance",
      title: "Compliance & Frameworks",
      sub: "Track security compliance with built-in framework support and evidence management",
      bullets: [
        { icon: <I.Shield />, text: "Built-in compliance framework tracking and assessments" },
        { icon: <I.FileText />, text: "Evidence collection with automated validation" },
        { icon: <I.Check />, text: "Vulnerability scanners and security findings" },
        { icon: <I.Users />, text: "Questionnaires for vendor risk assessments" },
      ],
      visual: <ComplianceViz />,
    },
    {
      id: "feat-mgmt", reverse: true, alt: true,
      title: "Centralized Server Management",
      sub: "Control all your infrastructure from a single dashboard",
      bullets: [
        { icon: <I.Server />, text: "Manage Windows and Linux servers from one view" },
        { icon: <I.Globe />, text: "Multi-tenant architecture for MSPs and enterprises" },
        { icon: <I.Users />, text: "Role-based access control with audit logging" },
        { icon: <I.Settings />, text: "Task management and REST API integration" },
      ],
      visual: <ManagementViz />,
    },
    {
      id: "feat-alerts",
      title: "Smart Alerting System",
      sub: "Never miss a critical event with intelligent notifications",
      bullets: [
        { icon: <I.Bell />, text: "Email, Slack, and Microsoft Teams notifications" },
        { icon: <I.TrendingUp />, text: "Anomaly detection for unusual patterns" },
        { icon: <I.Clock />, text: "Configurable alert schedules and escalation" },
        { icon: <I.Check />, text: "Alert acknowledgment and incident tracking" },
      ],
      visual: <AlertsViz />,
    },
  ];
  return (
    <div id="features">
      {rows.map((r) => <FeatureRow key={r.id} row={r} onExplore={onSignUp} />)}
    </div>
  );
}

function Testimonials() {
  const quotes = [
    { text: "ServSpect transformed our server monitoring. The zero-config agent deployment saved us hours of setup time, and the security features give us peace of mind.", role: "IT Operations Manager", company: "Enterprise Tech Co", hue: 0 },
    { text: "The Windows event log analysis is incredibly powerful. We catch security incidents before they become problems, and the alerting system is exactly what we needed.", role: "Security Engineer", company: "Financial Services", hue: 90 },
    { text: "As an MSP, we needed multi-tenant monitoring that scales. ServSpect delivers enterprise features at a price point that makes sense for our business.", role: "Managed Services Director", company: "IT Solutions Provider", hue: 180 },
  ];
  return (
    <section id="testimonials">
      <div className="container">
        <div className="section-head" style={{ textAlign: "center", margin: "0 auto 48px" }}>
          <span className="eyebrow">Testimonials</span>
          <h2 className="display">ServSpect powers teams <em>around the world.</em></h2>
          <p className="lede" style={{ margin: "14px auto 0" }}>Trusted by IT teams to monitor their critical infrastructure</p>
        </div>
        <div className="quotes">
          {quotes.map((q, i) => (
            <div key={i} className="quote">
              <div className="quote__mark">"</div>
              <p className="quote__text">{q.text}</p>
              <div className="quote__author">
                <div className="quote__avatar" style={{ ["--r" as any]: q.hue + "deg" }} />
                <div className="quote__meta">
                  <b>{q.role}</b>
                  <span>{q.company}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA({ onSignUp }: { onSignUp: () => void }) {
  const navigate = useNavigate();
  return (
    <section style={{ padding: "60px 0" }}>
      <div className="container">
        <div style={{
          position: "relative",
          padding: "80px 48px",
          borderRadius: 24,
          border: "1px solid oklch(0.6 0.15 var(--hue) / 0.5)",
          background: `radial-gradient(800px 400px at 20% -20%, oklch(0.5 0.18 var(--hue) / 0.5), transparent 60%), radial-gradient(600px 400px at 110% 120%, oklch(0.5 0.18 calc(var(--hue) + 60) / 0.4), transparent 60%), linear-gradient(180deg, oklch(0.22 0.05 var(--hue) / 0.9), oklch(0.16 0.04 var(--hue) / 0.95))`,
          textAlign: "center",
          overflow: "hidden",
        }}>
          <h2 className="display" style={{ margin: 0 }}>Ready to secure<br/>your <em>servers?</em></h2>
          <p className="lede" style={{ margin: "20px auto 28px" }}>Get started in minutes with our zero-config agent. No credit card required for the free tier.</p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn btn--primary" onClick={onSignUp}>Get started — free <I.ArrowRight /></button>
            <button className="btn" onClick={() => navigate("/install")}>View Installation Guide</button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="site">
      <div className="container" style={{ gridTemplateColumns: "2fr 1fr 1fr" }}>
        <div>
          <div className="nav__brand" style={{ marginBottom: 14 }}>
            <div className="nav__logo" />
            <span>ServSpect</span>
          </div>
          <p style={{ color: "var(--fg-3)", fontSize: 14, maxWidth: 440, margin: 0, lineHeight: 1.55 }}>
            Enterprise-grade server monitoring and security platform. Monitor performance, manage unified logs, track compliance, and protect your Windows and Linux infrastructure.
          </p>
        </div>
        <div>
          <h5>Product</h5>
          <ul>
            <li><Link to="/install">Installation</Link></li>
            <li><Link to="/dashboard">Dashboard</Link></li>
            <li><Link to="/api-docs">API Documentation</Link></li>
            <li><Link to="/knowledge-hub">Knowledge Hub</Link></li>
            <li><Link to="/demo">Demo</Link></li>
          </ul>
        </div>
        <div>
          <h5>Legal</h5>
          <ul>
            <li><Link to="/privacy">Privacy Policy</Link></li>
            <li><Link to="/terms">Terms of Service</Link></li>
            <li><Link to="/security">Security</Link></li>
          </ul>
        </div>
      </div>
      <div className="container copyright">
        <span>© {new Date().getFullYear()} ServSpect. All rights reserved.</span>
        <a href="https://github.com" target="_blank" rel="noreferrer noopener" style={{ color: "var(--fg-3)" }}><I.Github /></a>
      </div>
    </footer>
  );
}

/* ============================================================
   Modals — wired to real Supabase auth
============================================================ */
function SignInModal({ onClose }: { onClose: () => void }) {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true); setErr(null);
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get("email") || "");
    const password = String(fd.get("password") || "");
    const { error } = await signIn(email, password);
    setLoading(false);
    if (error) {
      setErr(error.message || "Invalid email or password.");
      return;
    }
    onClose();
    const params = new URLSearchParams(window.location.search);
    navigate(params.get("returnTo") || "/dashboard");
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close"><I.X /></button>
        <h3>Sign In to ServSpect</h3>
        <p>Access your server monitoring dashboard</p>
        {err && <div className="alert"><I.AlertTriangle /> {err}</div>}
        <div className="field">
          <label htmlFor="signin-email">Email</label>
          <input id="signin-email" name="email" type="email" placeholder="you@company.com" required autoComplete="email" />
        </div>
        <div className="field">
          <label htmlFor="signin-password">Password</label>
          <input id="signin-password" name="password" type="password" placeholder="••••••••" required autoComplete="current-password" />
        </div>
        <button className="btn btn--primary" style={{ width: "100%", justifyContent: "center", marginTop: 8 }} disabled={loading}>
          {loading ? "Signing in..." : "Sign In"}
        </button>
      </form>
    </div>
  );
}

function SignUpModal({ onClose, onSwitchToSignIn }: { onClose: () => void; onSwitchToSignIn: () => void }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={(e) => e.stopPropagation()} onSubmit={(e) => e.preventDefault()}>
        <button type="button" className="modal__close" onClick={onClose} aria-label="Close"><I.X /></button>
        <h3>Create Your Account</h3>
        <p>Start monitoring your infrastructure in minutes</p>
        <div className="alert"><I.AlertTriangle /> Account registration is currently disabled. Please check back soon!</div>
        <div className="field"><label>Full Name</label><input placeholder="Jane Doe" disabled /></div>
        <div className="field"><label>Tenant Name</label><input placeholder="Acme Corp" disabled /></div>
        <div className="field"><label>Email</label><input placeholder="you@company.com" disabled /></div>
        <div className="field"><label>Password</label><input type="password" placeholder="••••••••" disabled /></div>
        <button type="button" className="btn" style={{ width: "100%", justifyContent: "center", marginTop: 8, opacity: 0.6, cursor: "not-allowed" }} disabled>Coming Soon</button>
        <div style={{ marginTop: 14, textAlign: "center", fontSize: 13, color: "var(--fg-3)" }}>
          Already have an account?{" "}
          <button type="button" onClick={onSwitchToSignIn} style={{ background: "none", border: 0, color: "var(--accent)", cursor: "pointer", padding: 0, font: "inherit" }}>Sign in</button>
        </div>
      </form>
    </div>
  );
}

/* ============================================================
   Page
============================================================ */
export default function LandingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modal, setModal] = useState<null | "signin" | "signup">(null);

  // If already signed in, route to dashboard.
  useEffect(() => {
    if (user) {
      const params = new URLSearchParams(window.location.search);
      navigate(params.get("returnTo") || "/dashboard");
    }
  }, [user, navigate]);

  const openSignIn = () => setModal("signin");
  const openSignUp = () => setModal("signup");
  const close = () => setModal(null);

  return (
    <div className="landing-root">
      <div className="page-bg" />
      <div className="grid-bg" />
      <div className="noise" />
      <Banner />
      <Nav onSignIn={openSignIn} onSignUp={openSignUp} />
      <main>
        <Hero onSignUp={openSignUp} />
        <Integrations />
        <FeatureSections onSignUp={openSignUp} />
        <Testimonials />
        <FinalCTA onSignUp={openSignUp} />
      </main>
      <Footer />
      {modal === "signin" && <SignInModal onClose={close} />}
      {modal === "signup" && <SignUpModal onClose={close} onSwitchToSignIn={() => setModal("signin")} />}
    </div>
  );
}